import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { UniverseStatusChip } from "@storyworm/ssup-ui";
import { useUniverseListing } from "@storyworm/ssup-data/react";

interface Props {
  /** Cross-org admin view when true; otherwise scoped to {@link orgId}. */
  isAdmin: boolean;
  /** The partner's org id (from their JWT claim). Ignored for admins. */
  orgId?: string;
}

/**
 * The signed-in landing content: the universes a user may see. All data access
 * (the admin/partner branching, caching, loading/error state) lives in the
 * shared `useUniverseListing` hook — this component is render-only.
 */
export function UniverseList({ isAdmin, orgId }: Props) {
  const { universes, orgNames, isLoading, error } = useUniverseListing({ isAdmin, orgId });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CircularProgress size={18} />
        <Typography color="text.secondary">Loading universes…</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  if (universes.length === 0) {
    return (
      <Typography color="text.secondary">
        {isAdmin
          ? "No universes exist yet. They're created in the SSUP console."
          : "No universes are available for your organization yet."}
      </Typography>
    );
  }

  const sorted = [...universes].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Stack spacing={1.5}>
      {sorted.map((u) => (
        <Card
          key={u.id}
          variant="outlined"
          sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {u.name}
            </Typography>
            {isAdmin && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {orgNames[u.owner_id] ?? u.owner_id}
              </Typography>
            )}
          </Box>
          <UniverseStatusChip universe={u} />
        </Card>
      ))}
    </Stack>
  );
}
